import { useEffect, useMemo, useState } from "react";
import { AlertPopup } from "../../../components/Popups/AlertPopup";
import { useCloseSession } from "../../../hooks/useEndSession";
import { usePopup } from "../../../hooks/usePopup";
import { useLocalization } from "../../../stores/localizationContext";
import { useLiveSessionStore } from "../../../stores/useLiveSessionStore";
import "./EndSessionTimer.scss";

export const EndSessionTimer = () => {
  const { t } = useLocalization();
  const { handleCloseSession } = useCloseSession();
  const sessionDurationInMinutes = useLiveSessionStore(
    (state) => state.sessionDurationInMinutes
  );
  const sessionData = useLiveSessionStore((state) => state.sessionData);

  // Additional time allowed after planned duration (in minutes)
  const sessionAdditionalDuration = Number(
    import.meta.env.VITE_SESSION_ADDITIONAL_DURATION_IN_MINUTES
  );

  const [showSessionTimeUpPopup, setShowSessionTimeUpPopup] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isExtraTime, setIsExtraTime] = useState(false);

  // Memoize duration calculations to avoid re-calculating on every render
  const { plannedDurationInSeconds, totalAllowedSeconds } = useMemo(() => {
    const planned = sessionDurationInMinutes * 60;
    const total = (sessionDurationInMinutes + sessionAdditionalDuration) * 60;
    return { plannedDurationInSeconds: planned, totalAllowedSeconds: total };
  }, [sessionDurationInMinutes, sessionAdditionalDuration]);

  // Effect to handle all timer logic: initialization, ticking, and state changes
  useEffect(() => {
    if (!sessionData?.actualStartDate || !sessionData?.currentServerDate) {
      return;
    }

    // Calculate initial elapsed time based on server dates for accuracy
    const calculateInitialElapsedTime = () => {
      const startDate = Date.parse(sessionData.actualStartDate);
      const nowServerUTC = Date.parse(sessionData.currentServerDate!);
      const elapsedMs = nowServerUTC - startDate;
      const elapsedSec = Math.floor(elapsedMs / 1000);
      return Math.max(0, elapsedSec);
    };

    // Set the initial time
    const initialElapsed = calculateInitialElapsedTime();
    setElapsedSeconds(initialElapsed);

    // Set up the interval to increment the timer every second
    const intervalId = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, [sessionData?.actualStartDate, sessionData?.currentServerDate]);

  // Effect to handle session state changes based on the ticking timer
  useEffect(() => {
    // Don't run on the initial render before elapsedSeconds is calculated
    if (elapsedSeconds === 0 && plannedDurationInSeconds > 0) {
      return;
    }

    if (elapsedSeconds >= totalAllowedSeconds) {
      setShowSessionTimeUpPopup(true);
      setIsExtraTime(true); // Ensure extra time is true when session ends
    } else if (elapsedSeconds >= plannedDurationInSeconds) {
      setIsExtraTime(true);
    } else {
      setIsExtraTime(false);
    }
  }, [elapsedSeconds, plannedDurationInSeconds, totalAllowedSeconds]);

  // Memoize the popup content
  const sessionTimeUpPopupContent = useMemo(() => {
    if (!showSessionTimeUpPopup) return null;

    return (
      <AlertPopup
        message={t("session_ended")}
        confirmText={t("ok_label")}
        onConfirm={handleCloseSession}
      />
    );
  }, [showSessionTimeUpPopup, t, handleCloseSession]);

  const SessionTimeUpPopupComponent = usePopup(sessionTimeUpPopupContent);

  // Calculate the time to be displayed (either counting up or down)
  const getDisplayTime = () => {
    if (!isExtraTime) {
      // Counting up: Show elapsed time
      const minutes = Math.floor(elapsedSeconds / 60);
      const seconds = elapsedSeconds % 60;
      return { minutes, seconds };
    } else {
      // Counting down: Show remaining extra time
      const remainingSeconds = Math.max(
        0,
        totalAllowedSeconds - elapsedSeconds
      );

      const minutes = Math.floor(remainingSeconds / 60);
      const seconds = remainingSeconds % 60;
      return { minutes, seconds };
    }
  };

  const { minutes, seconds } = getDisplayTime();

  return (
    <>
      <div
        className={`end-session-timer${
          isExtraTime ? " end-session-timer--extra" : ""
        }`}
      >
        <p>
          {minutes.toString().padStart(2, "0")}
          <span> {t("minutes_short_label")} </span>
          {seconds.toString().padStart(2, "0")}
          <span> {t("seconds_short_label")} </span>
        </p>
        {/* <span className="time-value">
          {minutes.toString().padStart(2, "0")}
        </span>
        <span className="time-label">{t("minutes_short_label")}</span>
        <span className="time-value">
          {seconds.toString().padStart(2, "0")}
        </span>
        <span className="time-label">{t("seconds_short_label")}</span> */}
      </div>

      <SessionTimeUpPopupComponent />
    </>
  );
};
